// Safe markdown renderer for Mango Notes — bold, italic, bold+italic, bullet lists,
// line breaks, and optional query highlighting. Returns React elements only.
import React from "react";

function escapeRegExp(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function highlightNode(text, highlight, keyBase) {
  if (!text || !highlight) return text;
  const re = new RegExp(`(${escapeRegExp(highlight)})`, "ig");
  const parts = text.split(re);
  return parts.map((part, i) =>
    part && re.test(part)
      ? <mark key={`${keyBase}-h-${i}`} className="bg-amber-200/80 dark:bg-amber-500/40 text-inherit rounded px-0.5">{part}</mark>
      : part
  );
}

// Tokenize inline markdown. Order matters: longer markers first.
// Returns an array of {type, text} tokens.
function tokenize(text) {
  const tokens = [];
  const re = /(\*\*\*([^*\n]+)\*\*\*|___([^_\n]+)___|\*\*([^*\n]+)\*\*|__([^_\n]+)__|\*([^*\n]+)\*|_([^_\n]+)_)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) tokens.push({ type: "t", text: text.slice(last, m.index) });
    const tok = m[0];
    if (m[2] || m[3])      tokens.push({ type: "bi", text: m[2] || m[3] });
    else if (m[4] || m[5]) tokens.push({ type: "b",  text: m[4] || m[5] });
    else                   tokens.push({ type: "i",  text: m[6] || m[7] });
    last = m.index + tok.length;
  }
  if (last < text.length) tokens.push({ type: "t", text: text.slice(last) });
  return tokens;
}

function renderInline(text, { keyBase = "", highlight = "" } = {}) {
  const tokens = tokenize(text);
  return tokens.map((tok, i) => {
    const inner = highlight ? highlightNode(tok.text, highlight, `${keyBase}-${i}`) : tok.text;
    if (tok.type === "bi") return <strong key={`${keyBase}-bi-${i}`}><em>{inner}</em></strong>;
    if (tok.type === "b")  return <strong key={`${keyBase}-b-${i}`}>{inner}</strong>;
    if (tok.type === "i")  return <em key={`${keyBase}-i-${i}`}>{inner}</em>;
    return <React.Fragment key={`${keyBase}-t-${i}`}>{inner}</React.Fragment>;
  });
}

export function renderMarkdown(text, { className = "", highlight = "" } = {}) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listBuffer = [];
  let paraBuffer = [];
  let key = 0;

  const flushPara = () => {
    if (paraBuffer.length) {
      const joined = paraBuffer.join(" ");
      blocks.push(
        <p key={`p-${key++}`} className="mb-2 last:mb-0">
          {renderInline(joined, { keyBase: `p${key}`, highlight })}
        </p>
      );
      paraBuffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push(
        <ul key={`u-${key++}`} className="list-disc pl-5 mb-2 space-y-0.5 marker:text-stone-400">
          {listBuffer.map((item, idx) => (
            // Use the item's text as part of the key so re-ordered or
            // edited lists reconcile correctly instead of reusing stale DOM.
            <li key={`li-${key}-${idx}-${item.slice(0, 32)}`}>{renderInline(item, { keyBase: `li${key}-${idx}`, highlight })}</li>
          ))}
        </ul>
      );
      listBuffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushList(); continue; }
    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      flushPara();
      listBuffer.push(bullet[1]);
    } else {
      flushList();
      paraBuffer.push(line);
    }
  }
  flushPara();
  flushList();

  return <div className={className}>{blocks}</div>;
}

export function highlightText(text, highlight) {
  if (!text || !highlight) return text;
  return highlightNode(text, highlight, "ht");
}

export function hasMarkdown(text) {
  if (!text) return false;
  return /(\*\*|__|^[-*•]\s|\*\S|_\S)/m.test(text);
}
